import { z } from 'zod';
/**
 * Enums shared by the API, the apps and the database.
 *
 * Every enum here must exist in `packages/db/prisma/schema.prisma` with the
 * same values once its feature lands. `src/enums.sync.test.ts` enforces that
 * for the enums the schema already declares.
 */
export const ROLES = ['MEMBER', 'PROVIDER', 'ADMIN'];
export const Role = z.enum(ROLES);
export const USER_STATUSES = ['ACTIVE', 'SUSPENDED', 'DELETED'];
export const UserStatus = z.enum(USER_STATUSES);
export const CONSENT_KINDS = ['TERMS', 'PRIVACY', 'MARKETING', 'CONTACT_SHARE'];
export const ConsentKind = z.enum(CONSENT_KINDS);
export const LIVING_SITUATIONS = ['OWN', 'RENTING', 'OTHER'];
export const LivingSituation = z.enum(LIVING_SITUATIONS);
export const HEARD_FROMS = ['WORD_OF_MOUTH', 'SOCIAL_MEDIA', 'MARKETING', 'OTHER'];
export const HeardFrom = z.enum(HEARD_FROMS);
export const PROVINCES = [
    'EASTERN_CAPE',
    'FREE_STATE',
    'GAUTENG',
    'KWAZULU_NATAL',
    'LIMPOPO',
    'MPUMALANGA',
    'NORTH_WEST',
    'NORTHERN_CAPE',
    'WESTERN_CAPE',
];
export const Province = z.enum(PROVINCES);
/** Gauteng only at launch (D3). Other provinces may sign up for rewards and competitions. */
export const SERVED_PROVINCES = ['GAUTENG'];
export const PROVIDER_KINDS = ['SERVICE', 'ESTATE_AGENT', 'ATTORNEY'];
export const ProviderKind = z.enum(PROVIDER_KINDS);
/** A trade category belongs to one provider kind, so the directory can split service, evaluation and legal views. */
export const CATEGORY_KINDS = PROVIDER_KINDS;
export const CategoryKind = z.enum(CATEGORY_KINDS);
export const PROVIDER_STATUSES = [
    'DRAFT',
    'PENDING_REVIEW',
    'CHANGES_REQUESTED',
    'APPROVED',
    'REJECTED',
    'UNDER_INVESTIGATION',
    'PENDING_REMOVAL',
    'REMOVED',
];
export const ProviderStatus = z.enum(PROVIDER_STATUSES);
export const JOB_STATUSES = [
    'REQUESTED',
    'ACCEPTED',
    'DECLINED',
    'EXPIRED',
    'VERIFIED',
    'COMPLETED',
    'CANCELLED',
];
export const JobStatus = z.enum(JOB_STATUSES);
/** docs/product/domain.md, Subscription status. TRIALING is the member trial (D27). */
export const SUBSCRIPTION_STATUSES = [
    'PENDING',
    'TRIALING',
    'ACTIVE',
    'PAST_DUE',
    'CANCELLED',
    'EXPIRED',
];
export const SubscriptionStatus = z.enum(SUBSCRIPTION_STATUSES);
/** PROVIDER owners and plans wait on O26 (D1: member subscriptions only). */
export const BILLING_OWNER_TYPES = ['PROVIDER', 'MEMBER'];
export const BillingOwnerType = z.enum(BILLING_OWNER_TYPES);
export const PLAN_AUDIENCES = BILLING_OWNER_TYPES;
export const PlanAudience = z.enum(PLAN_AUDIENCES);
export const BILLING_INTERVALS = ['MONTH', 'YEAR'];
export const BillingInterval = z.enum(BILLING_INTERVALS);
export const PAYMENT_METHOD_STATUSES = ['ACTIVE', 'REVOKED'];
export const PaymentMethodStatus = z.enum(PAYMENT_METHOD_STATUSES);
export const INVOICE_STATUSES = ['OPEN', 'CHARGING', 'PAID', 'FAILED', 'VOID'];
export const InvoiceStatus = z.enum(INVOICE_STATUSES);
export const INVOICE_LINE_KINDS = ['PLAN', 'DISCOUNT'];
export const InvoiceLineKind = z.enum(INVOICE_LINE_KINDS);
export const PAYMENT_KINDS = ['CHARGE', 'REFUND'];
export const PaymentKind = z.enum(PAYMENT_KINDS);
export const PAYMENT_STATUSES = ['PENDING', 'SUCCEEDED', 'FAILED'];
export const PaymentStatus = z.enum(PAYMENT_STATUSES);
export const PROVIDER_DOCUMENT_KINDS = [
    'ID_DOC',
    'CIPC_REG',
    'PROOF_OF_ADDRESS',
    'TRADE_CERT',
    'INSURANCE',
    'AGREEMENT',
    'CONTRACT',
    'OTHER',
];
export const ProviderDocumentKind = z.enum(PROVIDER_DOCUMENT_KINDS);
export const PROVIDER_REVIEW_CHECKS = [
    'REG_NO_VALID',
    'ID_DOC',
    'PROOF_OF_ADDRESS',
    'TRADE_CERT',
    'INSURANCE',
    'PHOTO_OK',
    'PLACE_MATCHED',
];
export const ProviderReviewCheckKind = z.enum(PROVIDER_REVIEW_CHECKS);
export const REVIEW_STATUSES = ['PUBLISHED', 'HIDDEN'];
export const ReviewStatus = z.enum(REVIEW_STATUSES);
export const COMPLAINT_STATUSES = ['OPEN', 'NOTED', 'CLOSED'];
export const ComplaintStatus = z.enum(COMPLAINT_STATUSES);
export const OFFER_KINDS = ['CARD_CODE', 'INFO'];
export const OfferKind = z.enum(OFFER_KINDS);
export const AD_BANNER_SLOTS = ['DASHBOARD_1', 'DASHBOARD_2'];
export const AdBannerSlot = z.enum(AD_BANNER_SLOTS);
/**
 * Enum name in `schema.prisma` -> allowed values.
 * The sync test walks this registry, so adding an enum above is enough.
 */
export const ENUM_REGISTRY = {
    RoleType: ROLES,
    UserStatus: USER_STATUSES,
    ConsentKind: CONSENT_KINDS,
    LivingSituation: LIVING_SITUATIONS,
    HeardFrom: HEARD_FROMS,
    Province: PROVINCES,
    ProviderKind: PROVIDER_KINDS,
    CategoryKind: CATEGORY_KINDS,
    ProviderStatus: PROVIDER_STATUSES,
    JobStatus: JOB_STATUSES,
    SubscriptionStatus: SUBSCRIPTION_STATUSES,
    BillingOwnerType: BILLING_OWNER_TYPES,
    PlanAudience: PLAN_AUDIENCES,
    BillingInterval: BILLING_INTERVALS,
    PaymentMethodStatus: PAYMENT_METHOD_STATUSES,
    InvoiceStatus: INVOICE_STATUSES,
    InvoiceLineKind: INVOICE_LINE_KINDS,
    PaymentKind: PAYMENT_KINDS,
    PaymentStatus: PAYMENT_STATUSES,
    ProviderDocumentKind: PROVIDER_DOCUMENT_KINDS,
    ProviderReviewCheckKind: PROVIDER_REVIEW_CHECKS,
    ReviewStatus: REVIEW_STATUSES,
    ComplaintStatus: COMPLAINT_STATUSES,
    OfferKind: OFFER_KINDS,
    AdBannerSlot: AD_BANNER_SLOTS,
};
//# sourceMappingURL=enums.js.map